import React from 'react';

type CropTarget = 'cover' | 'qr';

type ImageCropDialogProps = {
  open: boolean;
  file: File | null;
  target: CropTarget;
  onCancel: () => void;
  onConfirm: (file: File) => void;
};

type ImageMetrics = {
  width: number;
  height: number;
};

const TARGET_CONFIG: Record<CropTarget, { aspectRatio: number; label: string; hint: string; outputWidth: number; outputHeight: number }> = {
  cover: {
    aspectRatio: 16 / 9,
    label: '封面裁剪',
    hint: '请裁剪为 16:9，适用于新闻和资源库封面。',
    outputWidth: 1600,
    outputHeight: 900,
  },
  qr: {
    aspectRatio: 1,
    label: '二维码裁剪',
    hint: '请裁剪为 1:1，适用于招聘二维码。',
    outputWidth: 1000,
    outputHeight: 1000,
  },
};

const VIEWPORT_WIDTH = 640;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const resolveCanvasType = (file: File) => {
  if (file.type === 'image/png' || file.type === 'image/jpeg' || file.type === 'image/webp') {
    return file.type;
  }
  return 'image/png';
};

const buildOutputName = (file: File, mimeType: string) => {
  const extension = mimeType === 'image/jpeg' ? 'jpg' : mimeType === 'image/webp' ? 'webp' : 'png';
  const originalName = file.name.replace(/\.[^.]+$/, '');
  return `${originalName}-cropped.${extension}`;
};

const ImageCropDialog: React.FC<ImageCropDialogProps> = ({ open, file, target, onCancel, onConfirm }) => {
  const [imageSrc, setImageSrc] = React.useState('');
  const [metrics, setMetrics] = React.useState<ImageMetrics | null>(null);
  const [zoom, setZoom] = React.useState(1);
  const [position, setPosition] = React.useState({ x: 0, y: 0 });
  const [dragging, setDragging] = React.useState(false);
  const [dragOrigin, setDragOrigin] = React.useState<{ pointerX: number; pointerY: number; startX: number; startY: number } | null>(null);
  const imageRef = React.useRef<HTMLImageElement | null>(null);
  const config = TARGET_CONFIG[target];
  const viewportHeight = Math.round(VIEWPORT_WIDTH / config.aspectRatio);

  const clampPosition = React.useMemo(() => {
    return (nextX: number, nextY: number, nextZoom: number) => {
      if (!metrics) return { x: 0, y: 0 };

      const baseScale = Math.max(VIEWPORT_WIDTH / metrics.width, viewportHeight / metrics.height);
      const renderedWidth = metrics.width * baseScale * nextZoom;
      const renderedHeight = metrics.height * baseScale * nextZoom;

      const minX = Math.min(0, VIEWPORT_WIDTH - renderedWidth);
      const minY = Math.min(0, viewportHeight - renderedHeight);

      return {
        x: clamp(nextX, minX, 0),
        y: clamp(nextY, minY, 0),
      };
    };
  }, [metrics, viewportHeight]);

  React.useEffect(() => {
    if (!open || !file) {
      setImageSrc('');
      setMetrics(null);
      setZoom(1);
      setPosition({ x: 0, y: 0 });
      setDragging(false);
      setDragOrigin(null);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setImageSrc(objectUrl);
    setZoom(1);
    setPosition({ x: 0, y: 0 });

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [file, open]);

  React.useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onCancel, open]);

  React.useEffect(() => {
    if (!metrics) return;

    const baseScale = Math.max(VIEWPORT_WIDTH / metrics.width, viewportHeight / metrics.height);
    const renderedWidth = metrics.width * baseScale;
    const renderedHeight = metrics.height * baseScale;
    setPosition({
      x: (VIEWPORT_WIDTH - renderedWidth) / 2,
      y: (viewportHeight - renderedHeight) / 2,
    });
  }, [metrics, viewportHeight]);

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const image = event.currentTarget;
    setMetrics({ width: image.naturalWidth, height: image.naturalHeight });
  };

  const handleZoomChange = (nextZoom: number) => {
    setZoom(() => {
      const adjustedZoom = clamp(nextZoom, 1, 3);
      setPosition((currentPosition) => clampPosition(currentPosition.x, currentPosition.y, adjustedZoom));
      return adjustedZoom;
    });
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!metrics) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(true);
    setDragOrigin({
      pointerX: event.clientX,
      pointerY: event.clientY,
      startX: position.x,
      startY: position.y,
    });
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging || !dragOrigin) return;

    const deltaX = event.clientX - dragOrigin.pointerX;
    const deltaY = event.clientY - dragOrigin.pointerY;
    setPosition(clampPosition(dragOrigin.startX + deltaX, dragOrigin.startY + deltaY, zoom));
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setDragging(false);
    setDragOrigin(null);
  };

  const handleConfirm = async () => {
    if (!file || !metrics || !imageRef.current) return;

    const mimeType = resolveCanvasType(file);
    const { outputWidth, outputHeight } = config;
    const canvas = document.createElement('canvas');
    canvas.width = outputWidth;
    canvas.height = outputHeight;

    const context = canvas.getContext('2d');
    if (!context) return;

    const baseScale = Math.max(VIEWPORT_WIDTH / metrics.width, viewportHeight / metrics.height);
    const sourceScale = baseScale * zoom;
    const sourceX = Math.max(0, -position.x / sourceScale);
    const sourceY = Math.max(0, -position.y / sourceScale);
    const sourceWidth = Math.min(metrics.width - sourceX, VIEWPORT_WIDTH / sourceScale);
    const sourceHeight = Math.min(metrics.height - sourceY, viewportHeight / sourceScale);

    context.drawImage(imageRef.current, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, outputWidth, outputHeight);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, mimeType, mimeType === 'image/jpeg' ? 0.92 : undefined);
    });

    if (!blob) return;

    onConfirm(new File([blob], buildOutputName(file, mimeType), { type: mimeType }));
  };

  if (!open || !file) return null;

  const imageStyle = metrics
    ? {
        width: `${metrics.width}px`,
        height: `${metrics.height}px`,
        transform: `translate(${position.x}px, ${position.y}px) scale(${Math.max(VIEWPORT_WIDTH / metrics.width, viewportHeight / metrics.height) * zoom})`,
        transformOrigin: 'top left',
      }
    : undefined;

  return (
    <div className="content-console-cropper-overlay" role="presentation" onClick={onCancel}>
      <div className="content-console-cropper-dialog" role="dialog" aria-modal="true" aria-label={config.label} onClick={(event) => event.stopPropagation()}>
        <div className="content-console-cropper-head">
          <div>
            <h3>{config.label}</h3>
            <p>{config.hint}</p>
          </div>
          <button type="button" className="content-console-secondary-button" onClick={onCancel}>取消</button>
        </div>

        <div className="content-console-cropper-stage">
          <div
            className={`content-console-cropper-viewport${dragging ? ' is-dragging' : ''}`}
            style={{ width: VIEWPORT_WIDTH, height: viewportHeight }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          >
            {imageSrc ? <img ref={imageRef} src={imageSrc} alt={config.label} onLoad={handleImageLoad} style={imageStyle} draggable={false} /> : null}
          </div>
        </div>

        <div className="content-console-cropper-controls">
          <label className="content-console-cropper-zoom">
            <span>缩放</span>
            <input type="range" min="1" max="3" step="0.01" value={zoom} onChange={(event) => handleZoomChange(Number(event.target.value))} />
          </label>
          <div className="content-console-cropper-actions">
            <button type="button" className="content-console-secondary-button" onClick={onCancel}>重新选择</button>
            <button type="button" className="content-console-primary-button" onClick={() => void handleConfirm()}>确认裁剪</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCropDialog;
